import { memo, type PropsWithChildren, useRef } from 'react';
import * as NavigationMenu from '@radix-ui/react-navigation-menu';
import { useLocation } from '@reach/router';
import { motion, MotionConfig } from 'framer-motion';
import styled from 'styled-components';

import { LinkWithQuery } from '@/components/global/LinkWithQuery';
import { ReactComponent as IconArrowUpRight } from '@/images/icons/icon-arrow-up-right.svg';
import { useIsHydrated } from '@/utils/ClientSuspense';

import { type NavItem as NavItemType } from './config';
import { useNavDropdownContext } from './NavDropdownProvider';

const NavigationMenuContent = styled(NavigationMenu.Content)`
  position: absolute;
  top: 0;
  left: 0;
  width: auto;
`;

const Grid = styled.div<{ $cols?: number }>`
  display: grid;
  grid-template-columns: repeat(${({ $cols }) => $cols || 1}, 1fr);
  padding: calc(4rem / 16);
`;

const NavItemLink = styled.a`
  position: relative;
  display: block;
  border: 0;
  background: none;
  border-radius: calc(1rem / 16);

  &:hover {
    background-color: ${({ theme }) => theme.palette.fgA500};
  }
`;

const NavItemLinkInner = styled.div`
  display: grid;
  gap: 0.125rem 0.68rem;
  grid-template-columns: max-content 1fr max-content;
  grid-template-areas:
    'icon title arrow'
    '. description arrow';
  align-content: flex-start;
  align-items: center;
  width: 38ch;
  padding: 0.6rem;
  user-select: none;

  & svg {
    display: block;
    grid-area: icon;
    width: calc(15rem / 16);
    color: ${({ theme }) => theme.palette.fg1200};
  }
`;

const NavItemTitle = styled.div`
  grid-area: title;
  font-size: ${({ theme }) => theme.fontSize.body.base};
  font-weight: ${({ theme }) => theme.fontWeight.NORMAL};
  line-height: ${({ theme }) => theme.lineHeight.body.base};
  color: ${({ theme }) => theme.palette.fgLoud};
`;

const NavItemDescription = styled.div`
  grid-area: description;
  /* padding-left: 0.125rem; */
  color: ${({ theme }) => theme.palette.fgA1200};
  font-weight: ${({ theme }) => theme.fontWeight.body};
  font-size: ${({ theme }) => theme.fontSize.body.s};
  line-height: ${({ theme }) => theme.lineHeight.body.s};
`;

const List = styled.ul``;

const ExternalArrow = styled.div`
  grid-area: arrow;
  display: grid;
  place-content: center;
  align-self: center;
  margin-left: auto;
  width: 2rem;
  aspect-ratio: 1 / 1;
  border-radius: 99px;
  /* background-color: ${({ theme }) => theme.palette.fgA200}; */
  color: ${({ theme }) => theme.palette.fg1200};

  & > * {
    grid-area: 1 / -1;
  }
`;

function ExternalLinkIcon() {
  return (
    <ExternalArrow style={{ overflow: 'clip' }}>
      <motion.div
        variants={{
          hovered: {
            x: 20,
            y: -20,
            scale: 0.8,
            opacity: 0,
            filter: 'blur(2px)'
          },
          base: {
            x: 0,
            y: 0,
            scale: 1,
            opacity: 1,
            filter: 'blur(0px)'
          }
        }}>
        <IconArrowUpRight style={{ display: 'block' }} />
      </motion.div>
      <motion.div
        variants={{
          hovered: {
            x: 0,
            y: 0,
            scale: 1,
            opacity: 1,
            filter: 'blur(0px)'
          },
          base: {
            x: -20,
            y: 20,
            scale: 0.8,
            opacity: 0,
            filter: 'blur(2px)'
          }
        }}>
        <IconArrowUpRight style={{ display: 'block' }} />
      </motion.div>
    </ExternalArrow>
  );
}

function MagneticNavLink({
  item
}: {
  item: Extract<
    NavItemType,
    { dropdown: unknown }
  >['dropdown']['lists'][number]['children'][number];
}) {
  const { pathname } = useLocation();
  const isExternal = item.to.includes('https:');

  return (
    <MotionConfig
      transition={{
        type: 'spring',
        duration: 0.4,
        bounce: 0.32
      }}>
      <NavigationMenu.Link active={pathname === item.to} asChild>
        <NavItemLink as={LinkWithQuery} isExternal={isExternal} to={item.to}>
          <NavItemLinkInner as={motion.div} initial="base" whileHover="hovered">
            {item.icon}
            <NavItemTitle>{item.title}</NavItemTitle>
            <NavItemDescription>{item.description}</NavItemDescription>
            {isExternal ? <ExternalLinkIcon /> : null}
          </NavItemLinkInner>
        </NavItemLink>
      </NavigationMenu.Link>
    </MotionConfig>
  );
}

function NavMenuDropdownContent({
  children,
  cols
}: PropsWithChildren<{ cols: number }>) {
  const isHydrated = useIsHydrated();
  const state = useNavDropdownContext();
  const ref = useRef<HTMLDivElement>(null);

  const radixId = ref.current?.id.split('-content-').at(-1);
  const offset = radixId ? state.relativeTransforms[radixId] : 0;
  const isOpen = state.value && ref.current?.id.includes(state.value);

  const optOutOfTransitionProps = state.shouldAnimateSize
    ? {}
    : { transition: { duration: 0 } };

  return (
    <NavigationMenuContent
      ref={ref}
      forceMount
      style={{
        opacity: isHydrated ? 1 : 0,
        width: isHydrated ? 'auto' : 0,
        height: isHydrated ? 'auto' : 0,
        pointerEvents: isOpen ? 'auto' : 'none'
      }}>
      <motion.div
        animate={{
          x: offset * 0.8
        }}
        {...optOutOfTransitionProps}>
        <Grid
          $cols={cols}
          animate={isOpen ? 'visible' : 'hidden'}
          as={motion.div}
          custom={{ isAnimatingSize: state.shouldAnimateSize }}
          variants={{
            visible: {
              opacity: 1
            },
            hidden: {
              opacity: 0
            }
          }}>
          {children}
        </Grid>
      </motion.div>
    </NavigationMenuContent>
  );
}

export const NavMenuDropdown = memo(function NavMenuDropdown({
  item
}: {
  item: Extract<NavItemType, { dropdown: unknown }>;
}) {
  return (
    <NavMenuDropdownContent cols={item.dropdown.lists.length}>
      {item.dropdown.lists.map(list => (
        <div key={list.title}>
          <List>
            {list.children.map(child => (
              <li key={child.title}>
                <MagneticNavLink item={child} />
              </li>
            ))}
          </List>
        </div>
      ))}
    </NavMenuDropdownContent>
  );
});
